import fetch from 'node-fetch';
import { Batch, BatchStatus, BatchType, BulkOperationJob, Document } from '../types/document';
import { getAuthToken, getBaseApiUrl } from './helpers';
import { arrayBufferToBlob } from '../utils/fileutils';

// Mirrors MAX_UPLOAD_BYTES in api/src/scribonline/server.ts (mirrored, not imported, since that
// module boots its own Fastify server on import). Only the multi-file / non-PDF path below is
// capped: it goes through the scribonline Fastify server's /imageupload, which shares the
// scanner's multipart fileSize limit. Single PDFs (base64 or S3 presigned post) are NOT capped —
// districts routinely upload 20–65 MB cumulative-folder PDFs (FLIGHT-1264). Refusing locally
// gives the user a specific message instead of whatever /imageupload returns.
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const MAX_UPLOAD_LABEL = '20 MB';

export class FileTooLargeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FileTooLargeError';
  }
}

export const assertNoOversizeFile = (files: File[]): void => {
  const oversizeFile = files.find((file) => file.size > MAX_UPLOAD_BYTES);
  if (!oversizeFile) return;
  const sizeMB = (Math.ceil((oversizeFile.size / (1024 * 1024)) * 10) / 10).toFixed(1);
  throw new FileTooLargeError(
    `"${oversizeFile.name}" is too large to upload (${sizeMB} MB, ${MAX_UPLOAD_LABEL} max). Split it into smaller documents and try again.`,
  );
};

export const getBatches = async (accountId: string, status?: BatchStatus, type?: BatchType): Promise<Batch[]> => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/batch`;
  const response = await fetch(url, {
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    return response.json() as Promise<Batch[]>;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const revertBatch = async (accountId: string, batchId: string) => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/batch/${batchId}?deleteDocs=true`;
  const response = await fetch(url, {
    headers: {
      Authorization: await getAuthToken(),
    },
    method: 'DELETE',
  });
  if (response.ok) {
    return {};
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const downloadBatchReport = async (accountId: string, batchId: string) => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/batch/${batchId}/report`;
  const response = await fetch(url, {
    headers: {
      Authorization: await getAuthToken(),
    },
  });

  if (response.ok) {
    Promise.resolve('ok');
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const getBatchDocStatus = async (accountId: string, batchId: string) => {
  const url = `${getBaseApiUrl()}/v1/api/archive/${accountId}/batch/${batchId}/status`;
  const response = await fetch(url, {
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    return response.json() as Promise<Batch>;
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const searchDocuments = async (
  accountId: string,
  query: string,
  types?: string[],
  curPage?: number,
  pageSize?: number,
  sort?: string | string[],
): Promise<{ documents: Document[]; count: number }> => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify({ query, types, curPage, pageSize, sort }),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/_search`, requestOptions);
  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const getDocument = async (accountId: string, documentid: string): Promise<Document> => {
  // call the server so we can check auth and get our cookies set
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/${documentid}`, {
    method: 'GET',
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

const multiImageContentUpload = async (accountId: string, documentId: string, files: File[], onUploadProgress?: (progress: number) => void) => {
  const formData = new FormData();
  for (const file of files) {
    let filename = file.name;
    if (file.type === 'application/pdf' && !filename.endsWith('pdf')) {
      filename = `${filename}.pdf`;
    }
    formData.append('file', file, filename);
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = () => {
      if (xhr.readyState == XMLHttpRequest.DONE) {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve(xhr.status);
        } else {
          reject(xhr.status);
        }
      }
    };
    xhr.upload.onerror = () => {
      console.error('Upload failed.', xhr.responseText);
      reject(xhr.status);
    };

    xhr.upload.onprogress = (event) => {
      console.log(`Uploaded ${event.loaded} of ${event.total} bytes`);
      if (onUploadProgress) {
        onUploadProgress(100 * (event.loaded / event.total));
      }
    };

    xhr.open('POST', `${getBaseApiUrl()}/server/${accountId}/${documentId}/imageupload`);
    xhr.send(formData);
  });
};

const parseFilesForUpload = async (accountId: string, documentId: string, files?: File | File[], onUploadProgress?: (progress: number) => void) => {
  if (!files) return undefined;

  if (!Array.isArray(files)) {
    files = [files];
  }

  if (files.length === 0) return undefined;

  // check if single pdf, if not we will need to concat
  if (files.length === 1 && files[0].type === 'application/pdf') {
    const file = files[0];
    //check if we need to use large content upload
    if (file.size > 4000000) {
      await uploadLargeDocumentContent(accountId, documentId, file, onUploadProgress);
      return undefined;
    } else {
      // if not large, we just upload direct
      return fileToBase64(file);
    }
  }

  assertNoOversizeFile(files);
  await multiImageContentUpload(accountId, documentId, files, onUploadProgress);
  return undefined;
};

export const saveDocument = async (accountId: string, document: Document, files?: File | File[], onUploadProgress?: (progress: number) => void) => {
  const content = await parseFilesForUpload(accountId, document.id, files, onUploadProgress);
  const authToken = await getAuthToken();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = () => {
      if (xhr.readyState == XMLHttpRequest.DONE) {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve(xhr.status);
        } else {
          reject(xhr.status);
        }
      }
    };

    xhr.upload.onerror = () => {
      console.error('Upload failed.', xhr.responseText);
      reject(xhr.status);
    };

    xhr.upload.onprogress = (event) => {
      console.log(`Uploaded ${event.loaded} of ${event.total} bytes`);
      if (onUploadProgress && content) {
        onUploadProgress(100 * (event.loaded / event.total));
      }
    };

    xhr.open('PUT', `${getBaseApiUrl()}/v1/api/archive/${accountId}/document/${document.id}`);
    xhr.setRequestHeader('Authorization', authToken);
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.send(
      JSON.stringify({
        content,
        ...document,
      }),
    );
  });
};

export const beginAsyncContentUpload = async (accountId: string, document: Partial<Document>) => {
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/${document.id}?asyncUpload=true`, {
    method: 'PUT',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...document,
    }),
  });
  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const getDocumentContent = async (accountId: string, documentid: string) => {
  // call the server so we can check auth and get our cookies set
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/${documentid}/content`, {
    method: 'GET',
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    const presignedUrl = await response.text();
    return fetchS3PresignedObject(presignedUrl);
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const getDocumentThumbnail = async (accountId: string, documentid: string) => {
  // call the server so we can check auth and get our cookies set
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/${documentid}/thumb`, {
    method: 'GET',
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    return response.text();
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const saveDocumentContent = async (accountId: string, documentId: string, document: ArrayBuffer, onUploadProgress?: (progress: number) => void) => {
  if (document.byteLength > 4000000) {
    const content = arrayBufferToBlob(document);
    return uploadLargeDocumentContent(accountId, documentId, content, onUploadProgress);
  }
  const content = arrayBufferToBase64(document);
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/${documentId}/content`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthToken(),
    },
    body: JSON.stringify({
      accountId,
      documentId,
      content,
    }),
  });

  if (response.ok) {
    return {};
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

const uploadLargeDocumentContent = async (accountId: string, documentId: string, document: File | Blob, onUploadProgress?: (progress: number) => void) => {
  const beginResponse = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/${documentId}/content?largeUpload`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthToken(),
    },
    body: JSON.stringify({
      accountId,
      id: documentId,
    }),
  });

  const presignedPostData = await beginResponse.json();

  const form = new FormData();
  Object.keys(presignedPostData.fields).forEach((key) => {
    form.append(key, presignedPostData.fields[key]);
  });

  form.append('file', document);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onreadystatechange = () => {
      if (xhr.readyState == XMLHttpRequest.DONE) {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve(xhr.status);
        } else {
          reject(xhr.status);
        }
      }
    };
    xhr.upload.onerror = () => {
      console.error('Upload failed.', xhr.responseText);
      reject(xhr.status);
    };

    xhr.upload.onprogress = (event) => {
      console.log(`Uploaded ${event.loaded} of ${event.total} bytes`);
      if (onUploadProgress) {
        onUploadProgress(100 * (event.loaded / event.total));
      }
    };

    xhr.open('POST', presignedPostData.url);

    xhr.send(form);
  });
};

export const getAnnotations = async (accountId: string, documentId: string) => {
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/annotations/${documentId}`, {
    method: 'GET',
    headers: {
      Authorization: await getAuthToken(),
    },
  });
  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const saveAnnotations = async (accountId: string, documentId: string, annotationJSON: any) => {
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/annotations/${documentId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthToken(),
    },
    body: JSON.stringify({
      annotationJSON,
    }),
  });
  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const fetchS3PresignedObject = async (url: string) => {
  const response = await fetch(url, {
    method: 'GET',
  });

  if (response.ok) {
    return response.arrayBuffer();
  } else if (response.status === 404) {
    return Promise.reject('Error 404');
  } else {
    return Promise.reject('Call Failed: ' + response.status);
  }
};

export const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result) {
        const content = reader.result as string;
        resolve(content.split(',')[1]);
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

export const concatAndPrint = async (accountId: string, documentIds: string[]) => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify({ accountId, documentIds: JSON.stringify(documentIds) }),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/concat`, requestOptions);
  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    // You can handle errors more specifically if the API returns a JSON error body
    return Promise.reject(new Error('API endpoint not found (404).'));
  } else {
    // Try to parse an error message from the response body if it exists
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

export const bulkUpdateDocuments = async (
  accountId: string,
  documentIds: string[],
  updates: { status?: string; permanent?: boolean },
): Promise<{ successful: number; failed: number; total: number }> => {
  const requestOptions = {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify({ documentIds, updates }),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/_bulk`, requestOptions);
  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    return Promise.reject(new Error('API endpoint not found (404).'));
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

export const bulkUpdateDocumentsByQuery = async (
  accountId: string,
  searchQuery: string,
  excludedIds: string[],
  updates: { status?: string; permanent?: boolean },
): Promise<{ successful: number; failed: number; total: number }> => {
  const requestOptions = {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify({ searchQuery, excludedIds, updates }),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/document/_bulk`, requestOptions);
  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    return Promise.reject(new Error('API endpoint not found (404).'));
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

/**
 * Create a bulk operation job (async) - for large operations
 */
export const createBulkOperationJob = async (
  accountId: string,
  searchQuery: string,
  excludedIds: string[],
  updates: { status?: string; permanent?: boolean },
): Promise<{ jobId: string; status: string; operation: string; message: string }> => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify({ searchQuery, excludedIds, updates }),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/bulk-operation/create`, requestOptions);
  if (response.ok) {
    return response.json();
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

/**
 * Create a trash operation job
 */
export const createTrashJob = async (
  accountId: string,
  searchQuery?: string,
  excludedIds?: string[],
  documentIds?: string[],
): Promise<{ jobId: string; status: string; operation: string; message: string }> => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify({ searchQuery, excludedIds, documentIds }),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/bulk-operation/trash`, requestOptions);
  if (response.ok) {
    return response.json();
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

/**
 * Create a purge operation job
 */
export const createPurgeJob = async (
  accountId: string,
  searchQuery?: string,
  excludedIds?: string[],
  documentIds?: string[],
): Promise<{ jobId: string; status: string; operation: string; message: string }> => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: await getAuthToken() },
    body: JSON.stringify({ searchQuery, excludedIds, documentIds }),
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/bulk-operation/purge`, requestOptions);
  if (response.ok) {
    return response.json();
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

/**
 * Get trash job status
 */
export const getTrashJobStatus = async (accountId: string, jobId: string): Promise<BulkOperationJob> => {
  const requestOptions = {
    method: 'GET',
    headers: { Authorization: await getAuthToken() },
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/bulk-operation/trash/${jobId}`, requestOptions);
  if (response.ok) {
    return response.json();
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

/**
 * Get purge job status
 */
export const getPurgeJobStatus = async (accountId: string, jobId: string): Promise<BulkOperationJob> => {
  const requestOptions = {
    method: 'GET',
    headers: { Authorization: await getAuthToken() },
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/bulk-operation/purge/${jobId}`, requestOptions);
  if (response.ok) {
    return response.json();
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

/**
 * Get bulk operation job status (legacy - uses generic endpoint)
 */
export const getBulkOperationJobStatus = async (accountId: string, jobId: string): Promise<BulkOperationJob> => {
  const requestOptions = {
    method: 'GET',
    headers: { Authorization: await getAuthToken() },
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/bulk-operation/${jobId}`, requestOptions);
  if (response.ok) {
    return response.json();
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};

/**
 * List bulk operation jobs for an account
 */
export const listBulkOperationJobs = async (accountId: string): Promise<{ jobs: BulkOperationJob[] }> => {
  const requestOptions = {
    method: 'GET',
    headers: { Authorization: await getAuthToken() },
  };
  const response = await fetch(`${getBaseApiUrl()}/v1/api/archive/${accountId}/bulk-operation`, requestOptions);
  if (response.ok) {
    return response.json();
  } else {
    const errorBody = await response.text();
    return Promise.reject(new Error(`Call Failed: ${response.status}. ${errorBody}`));
  }
};
